<template>
  <div>
    <THeading
      :level="3"
      tag="h2"
      class="mb-6"
    >
      {{ resolvedTitle }}
    </THeading>

    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
      <NuxtLink
        v-for="location in sortedLocations"
        :key="location.path"
        class="flex flex-col gap-1 max-w-full w-fit group"
        :to="location.path"
        :title="location.fullName"
      >
        <template v-if="nameDisplay === 'kindNameWithPreposition'">
          <span class="flex items-baseline gap-1">
            <TIcon
              :icon="['fal', 'location-dot']"
              class="shrink-0"
              fixed-width
            />
            <span class="font-medium hover:underline">
              {{ t(`nearbyLocations.RVRentals.rvRentalsInLocation`, {
                locationWithPreposition: location.nameWithPreposition,
              }) }}
              <span
                v-if="location?.rvCount ?? 0 > 0"
                class="text-sm text-gray-500"
              >({{ location.rvCount }})</span>
            </span>
          </span>
        </template>
        <template v-else>
          <span class="flex items-center gap-1 font-semibold max-w-full group-hover:underline min-w-0 underline md:no-underline">
            <TIcon
              :icon="['fal', 'location-dot']"
              class="shrink-0"
              fixed-width
            />
            <span class="truncate">
              {{ getNameDisplay(location) }}
            </span>
          </span>

          <span
            v-if="showDistance && location.distanceKm !== null"
            class="text-sm text-gray-500"
          >
            {{ t('distanceAway', getDistance(location.distanceKm)) }}
          </span>

          <span
            v-if="showRvCount"
            class="text-sm text-gray-500"
          >
            {{ t('rvRentals', location?.rvCount ?? 0) }}
          </span>
        </template>
      </NuxtLink>
    </div>
  </div>
</template>

<script setup lang="ts">
import { UNIT_MEASUREMENT } from '~/constants'
import type { NearbyLocation, NearbyLocationsKind, NearbyLocationsNameDisplay, NearbyLocationsVariant } from '~/components/general/nearby-locations/types'

const props = withDefaults(defineProps<{
  locations: NearbyLocation[]
  distanceUnit?: UNIT_MEASUREMENT
  title?: string
  kind?: NearbyLocationsKind
  variant?: NearbyLocationsVariant
  locationWithPreposition?: string
  rvTitle?: string
  showParent?: boolean
  nameDisplay?: NearbyLocationsNameDisplay
  noSort?: boolean
  showDistance?: boolean
  showRvCount?: boolean
}>(), {
  distanceUnit: UNIT_MEASUREMENT.KM,
})
const { t } = useI18n()

const resolvedTitle = computed(() => {
  if (props.title) {
    return props.title
  }
  if (!props.kind) {
    return ''
  }
  return t(`nearbyLocations.${props.kind}.${props.variant ?? 'default'}`, {
    rvTitle: props.rvTitle,
    locationWithPreposition: props.locationWithPreposition,
  })
})

const sortedLocations = computed(() => props.noSort ? props.locations : [...props.locations].sort(locationSort))

function locationSort(a: NearbyLocation, b: NearbyLocation) {
  const NameA = a.name
  const NameB = b.name
  const DistanceA = a.distanceKm ?? 0
  const DistanceB = b.distanceKm ?? 0

  const distanceSort = DistanceA - DistanceB
  let nameSort = 0

  if (NameA < NameB) {
    nameSort = -1
  }
  else if (NameA > NameB) {
    nameSort = 1
  }

  return distanceSort || nameSort
}

function getDistance(distanceKM: number) {
  const distance = props.distanceUnit === UNIT_MEASUREMENT.MILES ? kmToMi(distanceKM) : distanceKM

  return { distance: Math.ceil(distance), unit: props.distanceUnit }
}

function getNameDisplay(location: NearbyLocation) {
  switch (props.nameDisplay) {
    case 'nameAndParent':
      return location.shortName

    case 'nameOnly':
    default:
      return location.name
  }
}
</script>

<i18n src="~~/i18n/locales/common/nearby-locations.json" lang="json" />

<i18n lang="json">
{
  "en": {
    "distanceAway": "{distance} {unit} away",
    "rvRentals": "0 RV rentals | 1 RV rental | {count} RV rentals"
  },
  "fr": {
    "distanceAway": "à {distance} {unit}",
    "rvRentals": "0 VR en location | 1 VR en location | {count} VRs en location"
  }
}
</i18n>
